<template>
  <div ref="lang-switcher" class="group relative z-[999] select-none">
    <div
      :class="
        button({
          variant: 'outline',
          size: 'sm',
          class: 'align-center flex gap-2 max-md:w-8 max-md:px-0',
        })
      "
      tabindex="0"
      @click="toggleDropdown"
    >
      <img :src="selectedOption?.image" class="h-6 w-6" />
      <div class="max-md:hidden">{{ selectedOption?.text }}</div>
    </div>
    <transition name="fade">
      <div v-if="isDropdownOpen" :class="float({ class: 'absolute flex w-max flex-col p-4' })">
        <button
          v-for="option in options"
          :key="option.language"
          :class="
            button({ variant: 'link', size: 'sm', class: 'align-center justify-start gap-2' })
          "
          @click="selectOption(option)"
        >
          <img :src="option.image" class="h-6 w-6" />
          <div>{{ option.text }}</div>
        </button>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { getLanguage, Local } from '@/entities/auth/local.enum';
import { computed, ref, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { selectLanguage } from '@/hooks/use-temporary-locale';
import germany from '@/assets/country-flags/germany.png';
import spain from '@/assets/country-flags/spain.png';
import unitedStates from '@/assets/country-flags/united-states.png';
import { button } from '@carvia/theme/lib/cva/button';
import { float } from '@carvia/theme/lib/cva/float';
import { onClickOutside } from '@vueuse/core';

interface LanguageOption {
  language: Local;
  text: string;
  image: string;
}

const { locale } = useI18n({
  useScope: 'global',
});

const options: LanguageOption[] = [
  {
    language: Local.ES_ES,
    text: 'ES',
    image: spain,
  },
  {
    language: Local.EN_US,
    text: 'EN',
    image: unitedStates,
  },
  {
    language: Local.DE_DE,
    text: 'DE',
    image: germany,
  },
];

const langSwitcher = useTemplateRef('lang-switcher');
onClickOutside(langSwitcher, () => {
  isDropdownOpen.value = false;
});

const isDropdownOpen = ref(false);
const selectedOption = computed(() =>
  options.find((option) => getLanguage(option.language) === locale.value),
);

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
}

function selectOption(option: LanguageOption) {
  isDropdownOpen.value = false;
  selectLanguage(getLanguage(option.language));
}
</script>
